// types
import { DeleteAppointmentRequest } from 'generated/index';
import { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';

// utils
import { mutations, queryKeys } from 'RestAPIs/rapini';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import { CommandResult } from 'RestAPIs/activities';
import { AppointmentPayload } from './SmartActionDialog.types';
import { convertFormFieldDataToPayload } from './SmartActionDialog.utils';

// libs
import dayjs from 'dayjs';
import { useQueryClient } from 'react-query';

export const useAppointmentMutation = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: createAppointment, isLoading: createIsLoading } =
    mutations.useCreateAppointment();
  const { mutateAsync: updateAppointment, isLoading: updateIsLoading } =
    mutations.useUpdateAppointment();
  const { mutateAsync: deleteAppointment, isLoading: deleteIsLoading } =
    mutations.useDeleteAppointment();

  const execute = async (
    appointment: AppointmentFormFieldData,
    actionType: 'create' | 'update' | 'delete',
    suppressWarnings: boolean = false
  ) => {
    const createOrUpdatePayload = convertFormFieldDataToPayload(
      appointment,
      suppressWarnings
    );

    const deletePayload = {
      appointmentOccurrenceIdentifier: {
        entityId: appointment.id,
        originalOccurrenceStart: dayjs(appointment.start).toISOString()
      },
      editAppointmentType: appointment.editAppointmentType || 'ONLY_THIS'
    } satisfies DeleteAppointmentRequest;

    const payload = (
      actionType === 'delete' ? deletePayload : createOrUpdatePayload
    ) as AppointmentPayload;

    try {
      let response: Omit<CommandResult, 'notifications'> & {
        notifications: {
          text: string;
        }[];
      };

      if (actionType === 'create') {
        response = (await createAppointment(payload)) as unknown as Omit<
          CommandResult,
          'notifications'
        > & {
          notifications: {
            text: string;
          }[];
        };
      } else if (actionType === 'update') {
        response = (await updateAppointment(payload)) as unknown as Omit<
          CommandResult,
          'notifications'
        > & {
          notifications: {
            text: string;
          }[];
        };
      } else {
        response = (await deleteAppointment(payload)) as unknown as Omit<
          CommandResult,
          'notifications'
        > & {
          notifications: {
            text: string;
          }[];
        };
      }

      /**
       * TODO: supress warnings related responses should be handled in the error flow (409)
       */
      if (response.success === false && response.notifications.length > 0) {
        return Promise.resolve(response);
      }

      showSuccessSnackbar();

      await queryClient.invalidateQueries({
        queryKey: queryKeys.findAuditedCommands(
          dayjs().startOf('day').toISOString(),
          dayjs().endOf('day').toISOString()
        )
      });

      return Promise.resolve(response);
    } catch {
      showErrorSnackbar();
    }
  };

  return {
    execute,
    isLoading: createIsLoading || updateIsLoading || deleteIsLoading
  };
};
